import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import imageCompression from 'browser-image-compression';
import { getCroppedImg } from './utils/cropImage';

/**
 * Convertit une data URL en File.
 */
function dataURLtoFile(dataURL, filename) {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}

const DEFAULT_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.85,
  fileType: undefined, // keep jpeg after crop
};

/**
 * Modal : rogner puis compresser une image avant envoi.
 * - open: boolean
 * - imageFile: File | null (fichier sélectionné)
 * - onConfirm: (file: File) => void
 * - onCancel: () => void
 * - aspectRatio: number | undefined (ex: 1 pour carré, 16/9; undefined = libre)
 * - title: string
 * - compressionOptions: { maxSizeMB?, maxWidthOrHeight? } optionnel
 */
const ImageCropCompressModal = ({
  open,
  imageFile,
  onConfirm,
  onCancel,
  aspectRatio,
  title = 'Rogner et enregistrer l\'image',
  compressionOptions = {},
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const opts = { ...DEFAULT_COMPRESSION_OPTIONS, ...compressionOptions };

  useEffect(() => {
    if (!open || !imageFile) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setError(null);
      return;
    }

    // SVG : pas de rognage ni compression, on renvoie le fichier tel quel
    if (imageFile.type === 'image/svg+xml') {
      onConfirm(imageFile);
      onCancel();
      return;
    }

    const url = URL.createObjectURL(imageFile);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [open, imageFile]);

  const onCropComplete = useCallback((_, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setProcessing(true);
    setError(null);
    try {
      const dataUrl = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = dataURLtoFile(dataUrl, imageFile?.name?.replace(/\.[^.]+$/, '.jpg') || 'image.jpg');
      const compressed = await imageCompression(croppedFile, opts);
      onConfirm(compressed);
      onCancel();
    } catch (err) {
      console.error('Rognage/compression:', err);
      setError(err?.message || 'Erreur lors du rognage ou de la compression.');
    } finally {
      setProcessing(false);
    }
  }, [imageSrc, croppedAreaPixels, imageFile, opts, onConfirm, onCancel]);

  if (!open) return null;

  // Si pas d'image à afficher (ou déjà traité type SVG), ne rien rendre
  if (!imageSrc) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        boxSizing: 'border-box',
      }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          maxWidth: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#2c3e50' }}>
          {title}
        </div>
        <div style={{ position: 'relative', width: '100%', height: 400, minHeight: 300 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{ containerStyle: { borderRadius: 0 } }}
          />
        </div>
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 14, color: '#666' }}>
            Zoom :
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{ marginLeft: 8, verticalAlign: 'middle', width: 120 }}
            />
          </label>
        </div>
        {error && (
          <div style={{ padding: '0 20px 8px', color: '#c0392b', fontSize: 14 }}>
            {error}
          </div>
        )}
        <div style={{ padding: '12px 20px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onCancel} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={processing}
            style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#3498db', color: '#fff', cursor: processing ? 'not-allowed' : 'pointer' }}
          >
            {processing ? 'Traitement…' : 'Rogner et compresser'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropCompressModal;
